import React, { useEffect, useState, useMemo } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Plus, X, Ban, Pencil, CheckCheck, Layers } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const emptyLinea = () => ({ cuenta_id: "", centro_costo_id: "", glosa: "", debito: "", credito: "" });

const ESTADO_LABEL = {
  borrador: "Borrador",
  registrado: "Autorizado",
  anulado: "Anulado",
};

function estadoBadgeClass(estado) {
  if (estado === "registrado") return "bg-success/10 text-success";
  if (estado === "borrador") return "bg-warning/20 text-warning-foreground";
  return "bg-muted-foreground/20 text-muted-foreground";
}

export default function LibroDiario() {
  const [asientos, setAsientos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState({});
  const [centralOpen, setCentralOpen] = useState(false);
  const [centralForm, setCentralForm] = useState({ fecha: todayISO(), glosa: "", estado: "registrado" });
  const [centralSaving, setCentralSaving] = useState(false);

  const [fecha, setFecha] = useState(todayISO());
  const [glosa, setGlosa] = useState("");
  const [lineas, setLineas] = useState([emptyLinea(), emptyLinea()]);

  const load = async () => {
    try {
      const params = filtroEstado ? { estado: filtroEstado } : {};
      const [a, c, cc] = await Promise.all([
        api.get("/asientos", { params }),
        api.get("/cuentas"),
        api.get("/centros-costo"),
      ]);
      setAsientos(a.data);
      setCuentas(c.data);
      setCentrosCosto(cc.data || []);
      setSelected({});
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEstado]);

  const cuentasImputables = useMemo(
    () => cuentas.filter((c) => c.imputable).sort((a, b) => a.codigo.localeCompare(b.codigo)),
    [cuentas]
  );

  const totalDebito = lineas.reduce((s, l) => s + (parseFloat(l.debito) || 0), 0);
  const totalCredito = lineas.reduce((s, l) => s + (parseFloat(l.credito) || 0), 0);
  const balanced = totalDebito > 0 && Math.round(totalDebito) === Math.round(totalCredito);

  const borradoresSeleccionados = useMemo(
    () => Object.entries(selected).filter(([id, v]) => v && asientos.find((a) => a.id === id && a.estado === "borrador")).map(([id]) => id),
    [selected, asientos]
  );

  const updateLinea = (i, patch) => {
    const arr = [...lineas];
    arr[i] = { ...arr[i], ...patch };
    setLineas(arr);
  };

  const addLinea = () => setLineas([...lineas, emptyLinea()]);
  const removeLinea = (i) => {
    if (lineas.length <= 2) return;
    setLineas(lineas.filter((_, idx) => idx !== i));
  };

  const reset = () => {
    setEditingId(null);
    setFecha(todayISO());
    setGlosa("");
    setLineas([emptyLinea(), emptyLinea()]);
  };

  const openEdit = (asiento) => {
    if (asiento.estado !== "borrador") {
      toast.warning("Solo se pueden editar comprobantes en borrador");
      return;
    }
    setEditingId(asiento.id);
    setFecha(asiento.fecha);
    setGlosa(asiento.glosa);
    setLineas(
      asiento.lineas.map((l) => ({
        cuenta_id: l.cuenta_id,
        centro_costo_id: l.centro_costo_id || "",
        glosa: l.glosa || "",
        debito: l.debito ? String(l.debito) : "",
        credito: l.credito ? String(l.credito) : "",
      }))
    );
    setOpenDialog(true);
  };

  const buildPayload = () => ({
    fecha,
    glosa,
    lineas: lineas
      .filter((l) => l.cuenta_id)
      .map((l) => ({
          cuenta_id: l.cuenta_id,
          centro_costo_id: l.centro_costo_id || undefined,
          glosa: l.glosa || "",
          debito: parseFloat(l.debito) || 0,
          credito: parseFloat(l.credito) || 0,
        })),
  });

  const submit = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    try {
      if (editingId) {
        await api.put(`/asientos/${editingId}`, payload);
        toast.success("Comprobante actualizado");
      } else {
        await api.post("/asientos", payload);
        toast.success("Asiento registrado");
      }
      setOpenDialog(false);
      reset();
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const anular = async (id) => {
    if (!window.confirm("¿Anular este asiento?")) return;
    try {
      await api.post(`/asientos/${id}/anular`);
      toast.success("Asiento anulado");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const aprobar = async (id) => {
    try {
      await api.post(`/asientos/${id}/aprobar`);
      toast.success("Comprobante autorizado");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const aprobarMasivo = async () => {
    if (borradoresSeleccionados.length === 0) return;
    try {
      const { data } = await api.post("/asientos/aprobar-masivo", { asiento_ids: borradoresSeleccionados });
      toast.success(`${data.aprobados} comprobante(s) autorizado(s)`);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const aprobarTodos = async () => {
    if (!window.confirm("Se autorizarán TODOS los comprobantes en borrador de esta empresa (pasarán a Registrado y se reflejarán en informes y dashboard). ¿Continuar?")) return;
    try {
      const { data } = await api.post("/asientos/aprobar-todos", {});
      toast.success(`${data.aprobados} comprobante(s) autorizado(s)`);
      setSelected({});
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const centralizar = async () => {
    if (borradoresSeleccionados.length < 2) {
      toast.warning("Selecciona al menos 2 borradores para centralizar");
      return;
    }
    if (!centralForm.glosa.trim()) {
      toast.warning("Indica una glosa para el asiento centralizado");
      return;
    }
    setCentralSaving(true);
    try {
      const { data } = await api.post("/asientos/centralizar-borradores", {
        asiento_ids: borradoresSeleccionados,
        fecha: centralForm.fecha,
        glosa: centralForm.glosa,
        estado: centralForm.estado,
      });
      toast.success(`Centralizados ${data.fusionados} comprobantes → #${data.asiento.numero}`);
      setCentralOpen(false);
      setCentralForm({ fecha: todayISO(), glosa: "", estado: "registrado" });
      load();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setCentralSaving(false);
    }
  };

  const toggleSelect = (id, estado) => {
    if (estado !== "borrador") return;
    setSelected((s) => ({ ...s, [id]: !s[id] }));
  };

  const countBorradores = asientos.filter((a) => a.estado === "borrador").length;

  return (
    <Page>
      <PageHeader
        title="Libro diario"
        subtitle="Comprobantes contables — crear, editar borradores, autorizar y centralizar"
        testid="libro-diario-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {countBorradores > 0 && borradoresSeleccionados.length === 0 && (
              <button
                onClick={aprobarTodos}
                data-testid="aprobar-todos-button"
                className="inline-flex items-center gap-1.5 border border-success text-success hover:bg-success/10 text-sm px-3 py-1.5 rounded-sm"
              >
                <CheckCheck className="h-4 w-4" /> Autorizar todos los borradores ({countBorradores})
              </button>
            )}
            {borradoresSeleccionados.length > 0 && (
              <>
                <button
                  onClick={aprobarMasivo}
                  data-testid="aprobar-masivo-button"
                  className="inline-flex items-center gap-1.5 border border-success text-success hover:bg-success/10 text-sm px-3 py-1.5 rounded-sm"
                >
                  <CheckCheck className="h-4 w-4" /> Autorizar ({borradoresSeleccionados.length})
                </button>
                {borradoresSeleccionados.length >= 2 && (
                  <button
                    onClick={() => setCentralOpen(true)}
                    data-testid="centralizar-button"
                    className="inline-flex items-center gap-1.5 border border-warning-foreground text-warning-foreground hover:bg-warning/15 text-sm px-3 py-1.5 rounded-sm"
                  >
                    <Layers className="h-4 w-4" /> Centralizar ({borradoresSeleccionados.length})
                  </button>
                )}
              </>
            )}
            <Dialog open={openDialog} onOpenChange={(v) => { setOpenDialog(v); if (!v) reset(); }}>
              <DialogTrigger asChild>
                <button
                  data-testid="new-asiento-button"
                  className="inline-flex items-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm"
                >
                  <Plus className="h-4 w-4" /> Nuevo asiento
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>{editingId ? "Editar comprobante (borrador)" : "Nuevo asiento contable"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} data-testid="new-asiento-form">
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div>
                      <label className="label-caps block mb-1">Fecha</label>
                      <input
                        type="date"
                        required
                        data-testid="asiento-fecha"
                        value={fecha}
                        onChange={(e) => setFecha(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="label-caps block mb-1">Glosa</label>
                      <input
                        required
                        data-testid="asiento-glosa"
                        value={glosa}
                        onChange={(e) => setGlosa(e.target.value)}
                        placeholder="ej: Venta del día / Pago arriendo"
                        className="w-full px-3 py-2 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                      />
                    </div>
                  </div>

                  <div className="border border-border rounded-sm overflow-hidden">
                    <table className="acc-table">
                      <thead>
                        <tr>
                        <th>Cuenta</th>
                        <th>Centro costo</th>
                        <th>Glosa</th>
                          <th className="num" style={{ width: 130 }}>Débito</th>
                          <th className="num" style={{ width: 130 }}>Crédito</th>
                          <th style={{ width: 40 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineas.map((l, i) => (
                          <tr key={i}>
                            <td>
                              <Select value={l.cuenta_id} onValueChange={(v) => updateLinea(i, { cuenta_id: v })}>
                                <SelectTrigger data-testid={`linea-cuenta-${i}`} className="h-8 text-xs">
                                  <SelectValue placeholder="Seleccionar..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {cuentasImputables.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                      <span className="font-mono mr-2">{c.codigo}</span>{c.nombre}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                            </Select>
                          </td>
                          <td>
                            <Select value={l.centro_costo_id || ""} onValueChange={(v) => updateLinea(i, { centro_costo_id: v === "none" ? "" : v })}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">—</SelectItem>
                                {centrosCosto.filter((cc) => cc.activo !== false).map((cc) => (
                                  <SelectItem key={cc.id} value={cc.id}>
                                    <span className="font-mono mr-1">{cc.codigo}</span>{cc.nombre}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td>
                            <input
                              value={l.glosa}
                              onChange={(e) => updateLinea(i, { glosa: e.target.value })}
                              data-testid={`linea-glosa-${i}`}
                                className="w-full px-2 py-1 border border-border rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                value={l.debito}
                                onChange={(e) => updateLinea(i, { debito: e.target.value, credito: e.target.value ? "" : l.credito })}
                                data-testid={`linea-debito-${i}`}
                                className="w-full px-2 py-1 border border-border rounded-sm text-xs text-right font-mono focus:outline-none focus:ring-1 focus:ring-primary/30"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                value={l.credito}
                                onChange={(e) => updateLinea(i, { credito: e.target.value, debito: e.target.value ? "" : l.debito })}
                                data-testid={`linea-credito-${i}`}
                                className="w-full px-2 py-1 border border-border rounded-sm text-xs text-right font-mono focus:outline-none focus:ring-1 focus:ring-primary/30"
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                onClick={() => removeLinea(i)}
                                data-testid={`remove-linea-${i}`}
                                className="text-destructive hover:bg-destructive/10 p-1 rounded-sm"
                                disabled={lineas.length <= 2}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-background font-semibold">
                          <td colSpan={3} className="text-right pr-3 text-xs label-caps">Totales</td>
                          <td className="num text-success" data-testid="total-debito">{fmtCLP(totalDebito)}</td>
                          <td className="num text-destructive" data-testid="total-credito">{fmtCLP(totalCredito)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <button
                      type="button"
                      onClick={addLinea}
                      data-testid="add-linea"
                      className="text-sm text-primary hover:underline"
                    >
                      + Agregar línea
                    </button>
                    <div className="flex items-center gap-3">
                      <span
                        data-testid="asiento-balance-status"
                        className={`text-xs label-caps ${balanced ? "text-success" : "text-destructive"}`}
                      >
                        {balanced ? "Cuadrado" : `Diferencia: ${fmtCLP(totalDebito - totalCredito)}`}
                      </span>
                      <button
                        type="submit"
                        disabled={!balanced || !glosa}
                        data-testid="submit-asiento"
                        className="bg-primary hover:bg-success disabled:opacity-50 text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm"
                      >
                        {editingId ? "Guardar cambios" : "Registrar asiento"}
                      </button>
                    </div>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {[
          { value: "", label: "Todos" },
          { value: "borrador", label: `Borradores (${countBorradores})` },
          { value: "registrado", label: "Autorizados" },
          { value: "anulado", label: "Anulados" },
        ].map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFiltroEstado(f.value)}
            data-testid={`filtro-estado-${f.value || "todos"}`}
            className={`text-xs px-3 py-1.5 rounded-sm border ${
              filtroEstado === f.value
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : asientos.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm" data-testid="asientos-empty">
            No hay comprobantes en este filtro.
          </div>
        ) : (
          <table className="acc-table" data-testid="asientos-table">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th style={{ width: 70 }}>N°</th>
                <th style={{ width: 110 }}>Fecha</th>
                <th>Glosa / Detalle</th>
                <th className="num" style={{ width: 130 }}>Débito</th>
                <th className="num" style={{ width: 130 }}>Crédito</th>
                <th style={{ width: 100 }}>Estado</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {asientos.map((a) => (
                <React.Fragment key={a.id}>
                  <tr className="bg-background" data-testid={`asiento-row-${a.numero}`}>
                    <td>
                      {a.estado === "borrador" && (
                        <input
                          type="checkbox"
                          checked={!!selected[a.id]}
                          onChange={() => toggleSelect(a.id, a.estado)}
                          data-testid={`select-asiento-${a.numero}`}
                        />
                      )}
                    </td>
                    <td className="num font-semibold">#{a.numero}</td>
                    <td>{fmtDate(a.fecha)}</td>
                    <td className="font-medium">{a.glosa}</td>
                    <td className="num font-semibold">{fmtCLP(a.total_debito)}</td>
                    <td className="num font-semibold">{fmtCLP(a.total_credito)}</td>
                    <td>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] tracking-[0.1em] uppercase font-semibold ${estadoBadgeClass(a.estado)}`}
                      >
                        {ESTADO_LABEL[a.estado] || a.estado}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-0.5">
                        {a.estado === "borrador" && (
                          <>
                            <button
                              onClick={() => openEdit(a)}
                              data-testid={`editar-asiento-${a.numero}`}
                              className="text-primary hover:bg-primary/10 p-1 rounded-sm"
                              title="Editar"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => aprobar(a.id)}
                              data-testid={`aprobar-asiento-${a.numero}`}
                              className="text-success hover:bg-success/10 p-1 rounded-sm"
                              title="Autorizar"
                            >
                              <CheckCheck className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                        {a.estado === "registrado" && (
                          <button
                            onClick={() => anular(a.id)}
                            data-testid={`anular-asiento-${a.numero}`}
                            className="text-destructive hover:bg-destructive/10 p-1 rounded-sm"
                            title="Anular"
                          >
                            <Ban className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {a.lineas.map((l, idx) => (
                    <tr key={idx} className="text-muted-foreground">
                      <td></td>
                      <td></td>
                      <td></td>
                      <td>
                        <span className="font-mono text-[11px] text-foreground">{l.cuenta_codigo}</span>{" "}
                        <span className="text-[12px]">{l.cuenta_nombre}</span>
                        {l.centro_costo_codigo && (
                          <span className="text-[10px] bg-muted px-1 rounded ml-1">{l.centro_costo_codigo}</span>
                        )}
                        {l.glosa && <span className="text-[11px] text-muted-foreground/70 ml-2">— {l.glosa}</span>}
                      </td>
                      <td className="num text-[12px]">{l.debito > 0 ? fmtCLP(l.debito) : ""}</td>
                      <td className="num text-[12px]">{l.credito > 0 ? fmtCLP(l.credito) : ""}</td>
                      <td></td>
                      <td></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={centralOpen} onOpenChange={(v) => !centralSaving && setCentralOpen(v)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Centralizar comprobantes</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Se fusionarán <strong>{borradoresSeleccionados.length}</strong> borradores en un solo comprobante,
            sumando montos por cuenta contable.
          </p>
          <div className="space-y-3 mt-2">
            <div>
              <label className="label-caps block mb-1">Fecha del asiento centralizado</label>
              <input
                type="date"
                value={centralForm.fecha}
                onChange={(e) => setCentralForm((f) => ({ ...f, fecha: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-sm text-sm"
              />
            </div>
            <div>
              <label className="label-caps block mb-1">Glosa</label>
              <input
                value={centralForm.glosa}
                onChange={(e) => setCentralForm((f) => ({ ...f, glosa: e.target.value }))}
                placeholder="ej: Centralización ventas marzo 2026"
                className="w-full px-3 py-2 border border-border rounded-sm text-sm"
              />
            </div>
            <div>
              <label className="label-caps block mb-1">Estado al crear</label>
              <Select value={centralForm.estado} onValueChange={(v) => setCentralForm((f) => ({ ...f, estado: v }))}>
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="borrador">Borrador</SelectItem>
                  <SelectItem value="registrado">Autorizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setCentralOpen(false)} className="border border-border text-sm px-4 py-2 rounded-sm">
              Cancelar
            </button>
            <button
              type="button"
              onClick={centralizar}
              disabled={centralSaving}
              data-testid="confirm-centralizar"
              className="bg-primary hover:bg-success disabled:opacity-50 text-primary-foreground text-sm px-4 py-2 rounded-sm"
            >
              {centralSaving ? "Centralizando…" : "Centralizar"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
